/**
 * @module app.shared
 */

import { QueryComparisonOperator, QueryOperation, IQueryOperation, QuerySpecialDateTime, QuerySpecialDate } from './api-types';
import { IApiQueryFilter } from './api-filters';
import { encodeFieldName, encodeTaggedLiteral, encodeTaggedLiteralArray, mapQueryComparisonOperator } from './api-utilities';
import { DateValue, DateTimeValue } from 'app/shared/lib/date-time';
import { encodeQueryString } from 'app/shared/lib/utilities';

export interface IQueryDateTimeBetweenFilterOptions {
    negate: boolean;
}

export interface IQueryDateTimeOnFilterOptions {
    ignoreNullValue: boolean;
    negate: boolean;
}

export interface IQueryDateTimeFilterOptions {
    ignoreNullValue: boolean;
    negate: boolean;
    operator: QueryComparisonOperator;
}

function verifyDate(field: string, value: DateValue | string, parameter?: string): DateValue;
function verifyDate(field: string, value: DateValue | string | null, parameter?: string): DateValue | null;
function verifyDate(field: string, value: DateValue | string | null, parameter: string = 'value'): DateValue | null {
    if(value) {
        if(value instanceof Date) {
            // in case something gets bound to a Date and gets past the type checking
            throw new TypeError(`Query date '${parameter}' for '${field}' is a JavaScript Date object, which is not supported.`);
        }
        value = value instanceof DateValue ? value : new DateValue(value);
        if(!value.isValid()) {
            // validate actual format of date parameter
            throw new RangeError(`Query date '${parameter}' for '${field}' is not valid.`);
        }
        return value;
    }
    return null;
}

function verifyDateTime(field: string, value: DateTimeValue | string, parameter?: string): DateTimeValue;
function verifyDateTime(field: string, value: DateTimeValue | string | null, parameter?: string): DateTimeValue | null;
function verifyDateTime(field: string, value: DateTimeValue | string | null, parameter: string = 'value'): DateTimeValue | null {
    if(value) {
        if(value instanceof Date) {
            // in case something gets bound to a Date and gets past the type checking
            throw new TypeError(`Query date/time '${parameter}' for '${field}' is a JavaScript Date object, which is not supported.`);
        }
        value = value instanceof DateTimeValue ? value : new DateTimeValue(value);
        if(!value.isValid()) {
            // in case something gets bound to a Date and gets past the type checking
            throw new RangeError(`Query date/time '${parameter}' for '${field}' is not valid.`);
        }
        return value;
    }
    return null;
}

export class ApiQueryDateFilter implements IApiQueryFilter {

    public value: DateValue | null;

    constructor(public field: string, value: DateValue | string | null, public options: IQueryDateTimeFilterOptions) {
        this.value = verifyDate(field, value);
    }

    public toString(): string {
        const operation = mapQueryComparisonOperator(this.options.operator, this.options.negate);
        const encodedField = encodeFieldName(this.field);
        let encodedValue = 'null';
        if(this.value) {
            encodedValue = encodeTaggedLiteral('date', this.value.toJSON());
        }

        return operation.buildExpression(encodedField, encodedValue);
    }

}

export class ApiQuerySpecialDateFilter implements IApiQueryFilter {

    constructor(public field: string, public value: QuerySpecialDate | null, public options: IQueryDateTimeFilterOptions) { }

    public toString(): string {
        const operation = mapQueryComparisonOperator(this.options.operator, this.options.negate);
        const encodedField = encodeFieldName(this.field);
        let encodedValue = 'null';
        if(this.value === 'today') {
            encodedValue = encodeQueryString('today()');
        }

        return operation.buildExpression(encodedField, encodedValue);
    }

}

export class ApiQueryDateTimeFilter implements IApiQueryFilter {

    public value: DateTimeValue | null;

    constructor(public field: string, value: DateTimeValue | string | null, public options: IQueryDateTimeFilterOptions) {
        this.value = verifyDateTime(field, value);
    }

    public toString(): string {
        const operation = mapQueryComparisonOperator(this.options.operator, this.options.negate);
        const encodedField = encodeFieldName(this.field);
        let encodedValue = 'null';
        if(this.value) {
            encodedValue = encodeTaggedLiteral('dateTime', this.value.toJSON());
        }

        return operation.buildExpression(encodedField, encodedValue);
    }

}

export class ApiQuerySpecialDateTimeFilter implements IApiQueryFilter {

    constructor(public field: string, public value: QuerySpecialDateTime | null, public options: IQueryDateTimeFilterOptions) { }

    public toString(): string {
        const operation = mapQueryComparisonOperator(this.options.operator, this.options.negate);
        const encodedField = encodeFieldName(this.field);
        let encodedValue = 'null';
        if(this.value === 'now') {
            encodedValue = encodeQueryString('now()');
        }

        return operation.buildExpression(encodedField, encodedValue);
    }

}

export class ApiQueryDateTimeOnDateFilter implements IApiQueryFilter {

    public value: DateValue | null;

    constructor(public field: string, value: DateValue | string | null, public options: IQueryDateTimeOnFilterOptions) {
        this.value = verifyDate(field, value);
    }

    public toString(): string {
        const operation = new QueryOperation('in', 'infix').negate(this.options.negate);

        const encodedField = encodeFieldName(this.field);
        let encodedValue;
        if(this.value) {
            let current = this.value;;
            let tomorrow = this.value.add(1, 'days');
            // note: no 'Z' timezone specifier, the dateTime bounds are in tenant's time zone
            const range = [
                `${current.toJSON()}T00:00:00`,
                `${tomorrow.toJSON()}T00:00:00`
            ];
            encodedValue = encodeTaggedLiteralArray('dateTime', range);
        }
        else {
            encodedValue = encodeTaggedLiteralArray('dateTime', [null, null]);
        }

        return operation.buildExpression(encodedField, encodedValue);
    }

}

export class ApiQueryDateBetweenFilter implements IApiQueryFilter {

    public lowerBound: DateValue;
    public upperBound: DateValue | null;

    constructor(public field: string, lowerBound: DateValue | string, upperBound: DateValue | string | null, public options: IQueryDateTimeBetweenFilterOptions) {
        this.lowerBound = verifyDate(field, lowerBound, 'lowerBound');
        this.upperBound = verifyDate(field, upperBound, 'upperBound');
    }

    public toString(): string {
        let operation: IQueryOperation;

        const encodedField = encodeFieldName(this.field);
        let encodedValue = 'null';
        if(this.upperBound) {
            const range = [
                this.lowerBound.toJSON(),
                this.upperBound.toJSON()
            ];
            encodedValue = encodeTaggedLiteralArray('date', range);

            operation = new QueryOperation('in', 'infix').negate(this.options.negate);
        }
        else {
            encodedValue = encodeTaggedLiteral('date', this.lowerBound.toJSON());

            operation = mapQueryComparisonOperator('gte', this.options.negate);
        }

        return operation.buildExpression(encodedField, encodedValue);
    }

}

export class ApiQueryDateTimeBetweenFilter implements IApiQueryFilter {

    public lowerBound: DateTimeValue;
    public upperBound: DateTimeValue | null;

    constructor(public field: string, lowerBound: DateTimeValue | string, upperBound: DateTimeValue | string | null, public options: IQueryDateTimeBetweenFilterOptions) {
        this.lowerBound = verifyDateTime(field, lowerBound, 'lowerBound');
        this.upperBound = verifyDateTime(field, upperBound, 'upperBound');
    }

    public toString(): string {
        let operation: IQueryOperation;

        const encodedField = encodeFieldName(this.field);
        let encodedValue = 'null';
        if(this.upperBound) {
            const range = [
                this.lowerBound.toJSON(),
                this.upperBound.toJSON()
            ];
            encodedValue = encodeTaggedLiteralArray('dateTime', range);

            operation = new QueryOperation('in', 'infix').negate(this.options.negate);
        }
        else {
            encodedValue = encodeTaggedLiteral('dateTime', this.lowerBound.toJSON());

            operation = mapQueryComparisonOperator('gte', this.options.negate);
        }

        return operation.buildExpression(encodedField, encodedValue);
    }

}

export class ApiQueryDateTimeBetweenDatesFilter implements IApiQueryFilter {

    public lowerBound: DateValue;
    public upperBound: DateValue | null;

    constructor(public field: string, lowerBound: DateValue | string, upperBound: DateValue | string | null, public options: IQueryDateTimeBetweenFilterOptions) {
        this.lowerBound = verifyDate(field, lowerBound, 'lowerBound');
        this.upperBound = verifyDate(field, upperBound, 'upperBound');
    }

    public toString(): string {
        let operation: IQueryOperation;

        const encodedField = encodeFieldName(this.field);
        let encodedValue = 'null';
        if(this.upperBound) {
            // note: no 'Z' timezone specifier, the dateTime bounds are in tenant's time zone
            const range = [
                `${this.lowerBound.toJSON()}T00:00:00`,
                `${this.upperBound.add(1, 'days').toJSON()}T00:00:00`  //adding 1 day to the end so it includes the end date
            ];
            encodedValue = encodeTaggedLiteralArray('dateTime', range);

            operation = new QueryOperation('in', 'infix').negate(this.options.negate);
        }
        else {
            encodedValue = encodeTaggedLiteral('dateTime', `${this.lowerBound.toJSON()}T00:00:00`);

            operation = mapQueryComparisonOperator('gte', this.options.negate);
        }

        // TODO: Clean this up once the API has been adjusted to account for parentheses around 'in' logic
        return '(' + operation.buildExpression(encodedField, encodedValue) + ')';
    }

}

